import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, RotateCcw, Save } from "lucide-react";
import { RangeKeyboard } from "@/components/RangeKeyboard";
import { PartGuide } from "@/components/PartGuide";
import { openMic, detectPitch, freqToMidi, midiName } from "@/lib/mic";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const VOICE_PARTS = [
  { label: "Soprano", lo: 60, hi: 81, color: "#F472B6" },
  { label: "Alto", lo: 53, hi: 74, color: "#F59E0B" },
  { label: "Tenor", lo: 48, hi: 69, color: "#34D399" },
  { label: "Bass", lo: 40, hi: 64, color: "#60A5FA" },
];
const white = (m) => midiName(m).replace("#", "");
const suggest = (lo, hi) => VOICE_PARTS.map((p) => ({ p, s: Math.min(hi, p.hi) - Math.max(lo, p.lo) - Math.abs((lo + hi) / 2 - (p.lo + p.hi) / 2) * 0.6 }))
  .sort((a, b) => b.s - a.s)[0].p.label;
const toMidi = (name) => { const m = name.match(/^([A-G])(#?)(\d)$/); return (Number(m[3]) + 1) * 12 + [0, 2, 4, 5, 7, 9, 11]["CDEFGAB".indexOf(m[1])] + (m[2] ? 1 : 0); };

const STEPS = { low: "Sing your LOWEST comfortable note on 'ah' and hold it", high: "Now sing your HIGHEST comfortable note on 'ah' and hold it" };

export const VoiceRangeFinder = ({ testId = "voice-range" }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(null);
  const [live, setLive] = useState(null);
  const [low, setLow] = useState(null);
  const [high, setHigh] = useState(null);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  const mic = useRef(null);
  const raf = useRef(0);
  const samples = useRef([]);
  const tokRef = useRef(0);

  useEffect(() => {
    if (user) api.get("/me/voice-range").then(({ data }) => { if (data?.low) { setLow(toMidi(data.low)); setHigh(toMidi(data.high)); setSaved(true); } }).catch(() => {});
    return () => { tokRef.current += 1; cancelAnimationFrame(raf.current); mic.current?.stop(); };
  }, [user]);

  const stopListening = (which) => {
    cancelAnimationFrame(raf.current); mic.current?.stop(); mic.current = null; setStep(null); setLive(null);
    const v = samples.current.slice().sort((a, b) => a - b);
    if (v.length < 8) { setErr("I couldn't hear a steady note — try again a little louder, closer to the mic."); return; }
    const pick = which === "low" ? v[Math.floor(v.length * 0.15)] : v[Math.floor(v.length * 0.85)];
    if (which === "low") setLow(pick); else setHigh(pick);
    setSaved(false);
  };

  const listen = async (which) => {
    setErr(""); samples.current = [];
    const tok = ++tokRef.current;
    let m;
    try { m = await openMic(); } catch { setErr("Microphone access was blocked. Allow the microphone in your browser and try again."); return; }
    if (tok !== tokRef.current) { m.stop(); return; }
    mic.current = m;
    setStep(which);
    const started = performance.now();
    const loop = () => {
      const f = detectPitch(mic.current.read(), mic.current.ctx.sampleRate);
      if (f && f > 60 && f < 1300) { const m = freqToMidi(f); samples.current.push(m); setLive(m); }
      if (performance.now() - started > 5000) { stopListening(which); return; }
      raf.current = requestAnimationFrame(loop);
    };
    loop();
  };

  const ready = low != null && high != null && high > low;
  const part = ready ? suggest(low, high) : null;
  const save = () => api.put("/me/voice-range", { low: midiName(low), high: midiName(high), part }).then(() => setSaved(true)).catch(() => setErr("Couldn't save your range — please try again."));
  useEffect(() => { if (user && ready && !saved) save(); }, [low, high]); // eslint-disable-line react-hooks/exhaustive-deps
  const reset = () => { setLow(null); setHigh(null); setSaved(false); setErr(""); };
  const bands = [...VOICE_PARTS.map((p) => ({ label: `${p.label} ${midiName(p.lo)}–${midiName(p.hi)}`, lo: white(p.lo), hi: white(p.hi), color: p.color })),
    ...(ready ? [{ label: `Your range ${midiName(low)}–${midiName(high)}`, lo: white(Math.max(36, low)), hi: white(Math.min(84, high)), color: "#A78BFA" }] : [])];

  return (
    <div className="mt-5 rounded-2xl border border-primary/30 bg-card p-4 sm:p-6" data-testid={testId}>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Mic size={14} /> Voice Range Finder</p>
      <p className="mt-2 text-sm text-muted-foreground">Find your vocal range and suggested singing part. Warm up first, then sing each note on "ah" for about 5 seconds.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {["low", "high"].map((w) => (
          <button key={w} onClick={() => (step === w ? stopListening(w) : listen(w))} disabled={step && step !== w} data-testid={`${testId}-${w}`}
            className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition-colors ${step === w ? "border-primary bg-primary/15" : "border-border hover:border-primary"}`}>
            <span>
              <span className="block text-sm font-semibold">{w === "low" ? "1. Lowest note" : "2. Highest note"}</span>
              <span className="block text-xs text-muted-foreground">{step === w ? STEPS[w] : (w === "low" ? low : high) != null ? `Found: ${midiName(w === "low" ? low : high)}` : "Tap to start listening"}</span>
            </span>
            {step === w ? <Square size={16} className="text-primary" /> : <Mic size={16} className="text-primary" />}
          </button>
        ))}
      </div>
      {step && <p className="mt-3 text-center font-serif text-3xl text-primary" data-testid={`${testId}-live`}>{live != null ? midiName(live) : "…"}</p>}
      {low != null && high != null && high <= low && !step && <p className="mt-3 text-sm text-amber-400" data-testid={`${testId}-order-hint`}>Your highest note ({midiName(high)}) must be higher than your lowest ({midiName(low)}) — try the highest note again, sliding up as high as is comfortable. <button onClick={reset} className="ml-1 underline" data-testid={`${testId}-order-reset`}>Start again</button></p>}
      {err && <p className="mt-3 text-sm text-red-400" data-testid={`${testId}-error`}>{err}</p>}
      <div className="mt-4"><RangeKeyboard from="C2" to="C6" split="C4" bands={bands} testId={`${testId}-keyboard`} /></div>
      {ready && (
        <div className="mt-3 flex flex-wrap items-center gap-3" data-testid={`${testId}-result`}>
          <p className="text-sm">Your range: <strong>{midiName(low)}–{midiName(high)}</strong> · Suggested part: <strong className="text-primary" data-testid={`${testId}-part`}>{part}</strong></p>
          {user && <button onClick={save} disabled={saved} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-60" data-testid={`${testId}-save`}><Save size={13} /> {saved ? "Saved to your account" : "Save to my account"}</button>}
          <button onClick={reset} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-primary" data-testid={`${testId}-reset`}><RotateCcw size={12} /> Try again</button>
        </div>
      )}
      {ready && <PartGuide part={part} low={low} high={high} testId={`${testId}-guide`} />}
      <p className="mt-3 text-xs text-muted-foreground">Ranges are a guide — every voice is different, and your range grows with practice.</p>
    </div>
  );
};
